import React, { Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { LayoutTypes } from "../constants";
import { AppDispatch, RootState } from "../redux/store";
import DefaultLayout from "../layouts/Default";
import VerticalLayout from "../layouts/Vertical";
import DetachedLayout from "../layouts/Detached";
import HorizontalLayout from "../layouts/Horizontal";
import TwoColumnLayout from "../layouts/TwoColumn";

import {
  authProtectedFlattenRoutes,
  publicProtectedFlattenRoutes,
  otherRouter,
} from "./index";
import { APICore } from "../helpers/api/apiCore";
import Loader from "../components/Loader";
import { changeCountry } from "../redux/actions";

const AllRoutes = (props: object) => {
  const { layout } = useSelector((state: RootState) => ({
    layout: state.Layout,
  }));

  const getLayout = () => {
    let layoutCls = DetachedLayout;
    switch (layout.layoutType) {
      case LayoutTypes.LAYOUT_HORIZONTAL:
        layoutCls = HorizontalLayout;
        break;
      case LayoutTypes.LAYOUT_DETACHED:
        layoutCls = DetachedLayout;
        break;
      case LayoutTypes.LAYOUT_VERTICAL:
        layoutCls = VerticalLayout;
        break;
      default:
        layoutCls = TwoColumnLayout;
        break;
    }
    return layoutCls;
  };

  let Layout = getLayout();
  const api = new APICore();
  // Require both user and token so we never show protected pages with invalid/missing token
  let isAuthenticated = api.hasValidAuthSession();
  let loggedInUser = isAuthenticated ? api.getLoggedInUser() : null;
  // If we have user in session but no token (e.g. old/corrupted session), clear and force login
  if (api.getLoggedInUser() && !api.hasValidAuthSession()) {
    api.setLoggedInUser(null);
    isAuthenticated = false;
    loggedInUser = null;
  }

  const RoleBasedRedirect = () => {
    const dispatch = useDispatch<AppDispatch>();

    if (!isAuthenticated) {
      return <Navigate to="/auth/login" replace />;
    }

    if (loggedInUser && !loggedInUser?.isEmailVerified) {
      return <Navigate to="/auth/confirm" replace />;
    }

    if (loggedInUser && !loggedInUser?.isActivated) {
      return <Navigate to="/auth/activate" replace />;
    }

    switch (loggedInUser?.role) {
      case "admin":
        return <Navigate to="/dashboard" replace />;
      case "seller":
        return <Navigate to="/dashboard" replace />;
      case "supervisor":
        return <Navigate to="/orders/list" replace />;
      case "agent":
        dispatch(changeCountry(loggedInUser.country));
        return <Navigate to="/orders/list" replace />;
      case "moderator":
        dispatch(changeCountry(loggedInUser.country));
        return <Navigate to="/dashboard" replace />;
      case "delivery":
        dispatch(changeCountry(loggedInUser.country));
        return <Navigate to="/shippings/delivery/list" replace />;
      case "sourcing_agent":
        return <Navigate to="/sourcing/dashboard" replace />;
      case "account_manager":
        return <Navigate to="/account-manager/sellers" replace />;
      default:
        return <Navigate to="/auth/login" replace />;
    }
  };

  return (
    <React.Fragment>
      <Routes>
        <Route>
          {publicProtectedFlattenRoutes.map((route) => (
            <Route
              path={route.path}
              element={
                <DefaultLayout {...props} layout={layout}>
                  {route.element}
                </DefaultLayout>
              }
              key={route.path}
            />
          ))}
        </Route>

        <Route>
          {otherRouter.map((route) => {
            const hasAccess =
              !route.roles ||
              (loggedInUser?.role && route.roles.includes(loggedInUser.role));
            return (
              <Route
                path={route.path}
                element={
                  !isAuthenticated ? (
                    <Navigate
                      to={{
                        pathname: "/auth/login",
                        search: "next=" + route.path,
                      }}
                      replace
                    />
                  ) : !hasAccess ? (
                    <Navigate to="/" replace />
                  ) : (
                    <Suspense fallback={<Loader />}>{route.element}</Suspense>
                  )
                }
                key={route.path}
              />
            );
          })}
        </Route>

        <Route>
          {authProtectedFlattenRoutes.map((route) => {
            const hasAccess =
              !route.roles ||
              (loggedInUser?.role && route.roles.includes(loggedInUser.role));
            return (
              <Route
                path={route.path}
                element={
                  !isAuthenticated ? (
                    <Navigate
                      to={{
                        pathname: "/auth/login",
                      }}
                      replace
                    />
                  ) : !hasAccess ? (
                    <Navigate to="/" replace />
                  ) : (
                    <Layout {...props}>{route.element}</Layout>
                  )
                }
                key={route.path}
              />
            );
          })}
        </Route>

        <Route path="/" element={<RoleBasedRedirect />} />
        <Route path="*" element={<RoleBasedRedirect />} />
      </Routes>
    </React.Fragment>
  );
};

export default AllRoutes;
